import { useVirtualizer } from '@tanstack/react-virtual';
import { type MouseEvent, type ReactNode, type RefObject, type UIEvent, useEffect, useMemo } from 'react';
import type { WireLine } from '@/api/client';
import type { ColumnDef } from '@/lib/columns';
import { dayKey, dayLabel, LEVEL_LABEL, lineTime, messageText, type Zone } from '@/lib/format';
import { renderText } from '@/lib/text';

type Item = { type: 'line'; line: WireLine } | { type: 'marker'; label: string; key: string } | { type: 'day'; label: string; key: string };

const LINE_HEIGHT = 28;
const DAY_HEIGHT = 34;

export interface LogListProps {
    lines: WireLine[];
    fresh: ReadonlySet<string>;
    // Request dumps (95% of the API's lines) are left out unless asked for: the list keeps an even rhythm.
    showRequests: boolean;
    columns: readonly ColumnDef[];
    zone: Zone;
    wrap: boolean;
    // The range spans more than one day: a divider names each day.
    multiDay: boolean;
    // Live: the first `liveCount` lines arrived live, above a marker that says since when; the rest is the search.
    liveCount: number;
    liveLabel: string | null;
    // The pointer over the list, or the list scrolled away from its top: live holds new lines until both stop.
    onScrolledAway: (away: boolean) => void;
    selectedId: string | null;
    onSelect: (line: WireLine) => void;
    // A click on a column's cell keeps its value, ⌥-click excludes it.
    onTerm: (field: string, value: string, exclude: boolean) => void;
    privacy: boolean;
    words: string[];
    hasMore: boolean;
    loadingMore: boolean;
    onMore: () => void;
    // At the start of the range: search further back.
    extendLabel: string | null;
    onExtend: () => void;
    scrollRef: RefObject<HTMLDivElement | null>;
    scrollToId: string | null;
    empty: ReactNode;
}

// The lines, virtualized: 100 k rows scroll like ten. Live, a marker parts what arrived live from the search below it.
export function LogList(props: LogListProps): ReactNode {
    const { zone, multiDay, showRequests } = props;
    const items = useMemo<Item[]>(() => {
        const out: Item[] = [];
        let lastDay: string | null = null;
        const add = (lines: readonly WireLine[], part: string): void => {
            for (const line of lines) {
                if (!showRequests && line.kind === 'request') {
                    continue;
                }
                if (multiDay) {
                    const at = Date.parse(line.ts);
                    const day = dayKey(at, zone);
                    if (day !== lastDay) {
                        out.push({ type: 'day', label: dayLabel(at, zone), key: `day-${part}-${day}` });
                        lastDay = day;
                    }
                }
                out.push({ type: 'line', line });
            }
        };
        if (props.liveLabel) {
            add(props.lines.slice(0, props.liveCount), 'live');
            out.push({ type: 'marker', label: props.liveLabel, key: 'live-marker' });
            add(props.lines.slice(props.liveCount), 'search');
        } else {
            add(props.lines, 'search');
        }

        return out;
    }, [props.lines, props.liveCount, props.liveLabel, showRequests, multiDay, zone]);

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => props.scrollRef.current,
        estimateSize: (index) => (items[index]?.type === 'day' ? DAY_HEIGHT : LINE_HEIGHT),
        overscan: 24,
        getItemKey: (index) => {
            const item = items[index];

            return item?.type === 'line' ? item.line.id : (item?.key ?? index);
        },
    });
    const { onScrolledAway } = props;
    const listEvents = {
        onScroll: (event: UIEvent<HTMLDivElement>) => onScrolledAway(event.currentTarget.scrollTop > 8),
    };

    const virtualItems = virtualizer.getVirtualItems();
    const lastIndex = virtualItems.at(-1)?.index ?? 0;
    const { hasMore, loadingMore, onMore } = props;
    useEffect(() => {
        if (hasMore && !loadingMore && lastIndex >= items.length - 40) {
            onMore();
        }
    }, [lastIndex, items.length, hasMore, loadingMore, onMore]);

    useEffect(() => {
        if (!props.scrollToId) {
            return;
        }
        const index = items.findIndex((item) => item.type === 'line' && item.line.id === props.scrollToId);
        if (index >= 0) {
            virtualizer.scrollToIndex(index, { align: 'auto' });
        }
    }, [props.scrollToId, items, virtualizer]);

    if (items.length === 0) {
        return (
            <div className="list" ref={props.scrollRef} {...listEvents}>
                {props.empty}
            </div>
        );
    }

    const cell = (event: MouseEvent, field: string, value: string | null): void => {
        if (!value) {
            return;
        }
        event.stopPropagation();
        props.onTerm(field, value, event.altKey);
    };

    return (
        <div className={`list ${props.wrap ? 'wrap' : ''}`} ref={props.scrollRef} {...listEvents}>
            <div style={{ height: virtualizer.getTotalSize() + 52, position: 'relative' }}>
                {virtualItems.map((virtual) => {
                    const item = items[virtual.index] as Item;
                    const style = { position: 'absolute' as const, top: 0, left: 0, transform: `translateY(${virtual.start}px)` };
                    if (item.type === 'marker') {
                        return (
                            <div key={virtual.key} className="live-marker" style={style}>
                                {item.label}
                            </div>
                        );
                    }
                    if (item.type === 'day') {
                        return (
                            <div key={virtual.key} className="day-divider" style={style}>
                                {item.label}
                            </div>
                        );
                    }
                    const { line } = item;
                    const time = lineTime(line.ts, zone);
                    const secrets = line.user_id ? [line.user_id] : [];
                    const shown = new Set(props.columns.map((column) => column.key));

                    return (
                        <div
                            key={virtual.key}
                            data-index={virtual.index}
                            ref={props.wrap ? virtualizer.measureElement : undefined}
                            style={style}
                            className={`row lv-${line.level} ${props.selectedId === line.id ? 'sel' : ''} ${props.fresh.has(line.id) ? 'fresh-in' : ''}`}
                            onClick={() => props.onSelect(line)}
                        >
                            <span className="t">
                                {time.clock}
                                <span className="ms">{time.ms}</span>
                            </span>
                            <span className={`lv ${line.level}`}>{line.level === 'info' ? '' : (LEVEL_LABEL[line.level] ?? line.level)}</span>
                            <span className="svc">{line.service === 'ms-media' ? 'media' : line.service}</span>
                            <span className="mod" title={`${line.module} · ${line.logger}`}>
                                {line.module}
                            </span>
                            {props.columns.map((column) => {
                                const value = column.value(line);

                                return (
                                    <span
                                        key={column.key}
                                        className={`xc ${column.key} ${column.key === 'status' ? statusClass(line.http_status) : ''}`}
                                        title={value ? `${column.label.toLowerCase()}: ${value} · click keeps · ⌥-click excludes` : undefined}
                                        onClick={(event) => cell(event, column.field, column.filterValue ? column.filterValue(line) : value)}
                                    >
                                        {value === '' ? (
                                            <span className="none">·</span>
                                        ) : column.secret && props.privacy ? (
                                            <span className="redact">████████</span>
                                        ) : (
                                            value
                                        )}
                                    </span>
                                );
                            })}
                            <span className="msg">
                                {renderText(messageText(line.message), { privacy: props.privacy, secrets, words: props.words })}
                                {line.exception && !shown.has('exception') && <span className="chip err">{line.exception}</span>}
                                {line.http_status >= 400 && !shown.has('status') && <span className="chip">{line.http_status}</span>}
                                {line.user_id && line.kind !== 'request' && !shown.has('user') && (
                                    <span className="chip">
                                        user {props.privacy ? <span className="redact">████████</span> : line.user_id.slice(0, 8)}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
                <div className="more" style={{ position: 'absolute', top: virtualizer.getTotalSize(), left: 0, right: 0 }}>
                    {props.loadingMore ? (
                        <span>Loading older lines…</span>
                    ) : props.hasMore ? (
                        <span>Older lines load as you scroll</span>
                    ) : props.extendLabel ? (
                        <>
                            <span>The start of the time range</span>
                            <button className="act" onClick={props.onExtend}>
                                {props.extendLabel}
                            </button>
                        </>
                    ) : (
                        <span>The start of the time range</span>
                    )}
                </div>
            </div>
        </div>
    );
}

const statusClass = (status: number): string => (status >= 500 ? 's5' : status >= 400 ? 's4' : '');
